import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot, faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import zodiacWheelData from '../../../questions/astrology/zodiacwheel.json'; 
import Question from '../../../components/Question';

const EclipticVsCelestialLesson = ({ level = 'beginner', onNextLesson, markLessonComplete, currentQuestion = 0, onQuestionChange, setCompletedQuestions, completedQuestions = [] }) => {
  const lessonData = zodiacWheelData[0]?.lessons[1]; // Lesson 2: Understanding the Ecliptic and Celestial Sphere
  const sections = lessonData?.sections || [];

  // Get all questions from all sections
  const allQuestions = sections.flatMap(section => section.questions || []);

  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');

  const handleQuestionChange = (newQuestionIndex) => {
    onQuestionChange(newQuestionIndex);
  };

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      // Update the parent's completed questions state
      setCompletedQuestions(prev => {
        const newCompleted = [...prev];
        newCompleted[currentQuestion] = true;
        return newCompleted;
      });
      
      // Wait a moment before moving to next question
      setTimeout(() => {
        if (currentQuestion === allQuestions.length - 1) {
        markLessonComplete();
        onNextLesson();
      } else {
          handleQuestionChange(currentQuestion + 1);
      }
      }, 1500);
    }
  };

  const handleDotClick = (index) => {
    if (index <= currentQuestion || completedQuestions[index - 1]) {
      handleQuestionChange(index);
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  const currentQuestionData = allQuestions[currentQuestion];

  // Find which section this question belongs to for content display
  let currentSectionData = null;
  let questionIndexInSection = 0;
  
  for (const section of sections) {
    if (section.questions && section.questions.length > 0) {
      if (questionIndexInSection + section.questions.length > currentQuestion) {
        currentSectionData = section;
        break;
      }
      questionIndexInSection += section.questions.length;
    }
  }

  return (
    <div>
      <Helmet>
        <title>{lessonData?.title || 'Ecliptic vs Celestial Lesson'} - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && lessonData?.imageUrl && (
            <img
              src={lessonData.imageUrl}
              alt={lessonData.title}
              className={imageSize}
            />
          )}
          <div className="icon-buttons">
            <FontAwesomeIcon
              icon={faEyeSlash}
              className="icon-button"
              onClick={toggleImageVisibility}
            />
            <FontAwesomeIcon
              icon={faExpandArrowsAlt}
              className="icon-button"
              onClick={toggleImageSize}
            />
          </div>

          {currentSectionData && (
            <section className="section has-text-centered" id={currentSectionData.id}>
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <h2 className="has-text-primary">{currentSectionData.title}</h2>
                    <p className="has-text-white">{currentSectionData.content}</p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {currentQuestionData && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={currentQuestionData.question}
                      options={currentQuestionData.options}
                      correctAnswer={currentQuestionData.correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="section has-text-centered">
            <div className="column">
              <div className="dots mt-4">
                {allQuestions.map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={completedQuestions[index] ? faCircleDot : faCircle}
                    className={`mr-2 ${index === currentQuestion ? 'has-text-primary' : completedQuestions[index] ? 'has-text-warning' : 'has-text-grey'} dot-icon`}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </div>
              <div className="mt-2">
                <p className="has-text-warning has-text-weight-bold">
                  {completedQuestions.filter(Boolean).length} of {allQuestions.length} questions in this lesson completed
                </p>
              </div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default EclipticVsCelestialLesson;